import React from 'react';
import { Dropdown, MenuProps } from 'antd';
import {
  DotsIcon,
  EyeFillIcon,
  PencilIcon,
  RefreshClockIcon,
} from '@/assets/icons/icons';
import BlockIcon from '@/assets/images/greyIcons/block.svg';
import refreshIcon from '@/assets/images/settingsusers/refreshblack.svg';
import addblack from '@/assets/images/settingsusers/addblack.svg';

export interface SettingsUserRow {
  id?: string | number;
  is_active?: boolean;
  is_pending?: boolean;
  permissions?: {
    is_update?: boolean;
    is_delete?: boolean;
  };
}

interface UserActionsMenuProps {
  record: SettingsUserRow;
  /** Activity is admin-only, independent of the row's edit/delete permissions. */
  isCompanyAdmin: boolean;
  onEdit: (record: SettingsUserRow) => void;
  onResendInvite: (record: SettingsUserRow) => void;
  onDisable: (record: SettingsUserRow) => void;
  onDeletePending: (record: SettingsUserRow) => void;
  onEnable: (record: SettingsUserRow) => void;
  onActivity: (record: SettingsUserRow) => void;
  /** Company owner, outside production, not this row — caller resolves the check. */
  canViewAsUser?: boolean;
  onViewAsUser?: (record: SettingsUserRow) => void;
}

// Static single-colour black glyphs: zl-invert-on-dark flips them in dark mode.
const MenuIcon: React.FC<{ src: string }> = ({ src }) => (
  <img
    src={src}
    alt=''
    className='settings-users-menu-icon zl-invert-on-dark'
  />
);

const UserActionsMenu: React.FC<UserActionsMenuProps> = ({
  record,
  isCompanyAdmin,
  onEdit,
  onResendInvite,
  onDisable,
  onDeletePending,
  onEnable,
  onActivity,
  canViewAsUser,
  onViewAsUser,
}) => {
  const canUpdate = !!record?.permissions?.is_update;
  const canDelete = !!record?.permissions?.is_delete;
  const isActive = !!record?.is_active;
  const isPending = !!record?.is_pending;
  // Invited-but-never-accepted users have no activity history yet.
  const canSeeActivity = isCompanyAdmin && !(isActive && isPending);

  const items: MenuProps['items'] = [];

  if (isActive && !isPending) {
    if (canUpdate) {
      items.push({
        key: 'edit',
        label: 'Edit',
        icon: <PencilIcon color='var(--text-primary)' />,
        onClick: () => onEdit(record),
      });
    }
    if (canSeeActivity) {
      items.push({
        key: 'activity',
        label: 'Activity',
        icon: (
          <RefreshClockIcon
            color='var(--text-primary)'
            width={14}
            height={14}
          />
        ),
        onClick: () => onActivity(record),
      });
    }
    if (canDelete) {
      items.push({
        key: 'disable',
        label: 'Disable',
        icon: <MenuIcon src={BlockIcon} />,
        danger: true,
        onClick: () => onDisable(record),
      });
    }
  } else if (isActive && isPending) {
    if (canUpdate) {
      items.push({
        key: 'resend',
        label: 'Resend invite',
        icon: <MenuIcon src={refreshIcon} />,
        onClick: () => onResendInvite(record),
      });
    }
    if (canDelete) {
      items.push({
        key: 'delete',
        label: 'Delete',
        icon: <MenuIcon src={BlockIcon} />,
        danger: true,
        onClick: () => onDeletePending(record),
      });
    }
  } else if (!isActive) {
    if (canDelete) {
      items.push({
        key: 'invite',
        label: 'Invite',
        icon: <MenuIcon src={addblack} />,
        onClick: () => onEnable(record),
      });
    }
    if (canSeeActivity) {
      items.push({
        key: 'activity',
        label: 'Activity',
        icon: (
          <RefreshClockIcon
            color='var(--text-primary)'
            width={14}
            height={14}
          />
        ),
        onClick: () => onActivity(record),
      });
    }
  }

  if (canDelete && canViewAsUser && onViewAsUser) {
    items.push({
      key: 'view_as_user',
      label: 'View as user',
      icon: <EyeFillIcon className='settings-users-menu-eye-icon' />,
      onClick: () => onViewAsUser(record),
    });
  }

  if (items.length === 0) return null;

  return (
    <Dropdown
      menu={{ items }}
      trigger={['click']}
      placement='bottomRight'
      overlayClassName='settings-users-menu-overlay'
    >
      <span
        className='user-actions-menu-trigger'
        onClick={(e) => e.stopPropagation()}
      >
        <DotsIcon color='var(--text-primary)' />
      </span>
    </Dropdown>
  );
};

export default UserActionsMenu;
